<template>
  <section>
    <h2 class="text-lg font-semibold mb-4">
      <span class="text-[#18b7bd]">2.</span> Choix de la date &amp; heure
    </h2>

<div v-if="employes.length > 1" class="mb-4">
  <label for="employeSelect" class="block mb-2 text-sm font-medium text-gray-700">
    Filtrer par employé :
  </label>
  <div class="flex flex-nowrap gap-3 sm:gap-4 mb-6 overflow-x-auto overflow-y-hidden">
    <div
      @click="clearSelectedEmploye"
      class="flex-shrink-0 w-36 sm:w-40 p-3 rounded-2xl shadow cursor-pointer flex items-center justify-center text-gray-700 font-semibold border-2
      transition-colors duration-200
      "
      :class="{
        'border-blue-500 bg-blue-50': !selectedEmployeId,
        'border-gray-200 bg-white': selectedEmployeId,
      }"
    >
      Peu importe
    </div>

    <div
      v-for="employe in employes"
      :key="employe._id"
      @click="selectEmploye(employe._id)"
      class="flex-shrink-0 w-44 sm:w-60 px-2 py-2 rounded-2xl shadow cursor-pointer flex items-center gap-3 sm:gap-4 border-2 transition-colors duration-200"
      :class="{
        'border-blue-500 bg-blue-50': selectedEmployeId === employe._id,
        'border-gray-200 bg-white': selectedEmployeId !== employe._id,
      }"
    >
      <div
        class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden text-lg sm:text-xl font-semibold text-white flex-shrink-0"
        :style="{ backgroundColor: !employe.photo ? stringToColor(employe.prenom || employe.nom) : 'transparent' }"
      >
        <img v-if="employe.photo" :src="employe.photo" alt="Avatar" class="w-full h-full object-cover" />
        <span v-else>{{ (employe.prenom || employe.nom)?.charAt(0).toUpperCase() }}</span>
      </div>
      <div class="text-xs sm:text-sm font-medium text-gray-800 whitespace-nowrap">
        {{ employe.prenom }} {{ employe.nom }}
      </div>
    </div>
  </div>
</div>


    <div class="bg-white rounded-xl shadow-md p-5 overflow-x-auto no-scrollbar" style="scroll-behavior: smooth;">
      <div class="flex flex-col gap-4">
        <!-- Navigation -->
        <div v-if="!selectedDate" class="flex items-center justify-center gap-4">
          <button
            aria-label="Précédent"
            class="text-indigo-200 hover:text-indigo-400 transition text-xl"
            @click="changeWeek(-1)"
            :disabled="isPreviousDisabled"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="text-sm text-gray-700 font-medium">
            Du {{ formatDate(visibleDays[0]?.date) }} au {{ formatDate(visibleDays[visibleDays.length - 1]?.date) }}
          </div>
          <button
            aria-label="Suivant"
            class="text-[#18b7bd] hover:text-indigo-800 transition text-xl"
            @click="changeWeek(1)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <!-- Créneaux -->
        <div class="flex gap-12 flex-nowrap justify-center">
          <template v-if="selectedDate">
            <div class="flex flex-col items-center min-w-[80px]">
              <div class="text-xs text-gray-900 font-normal mb-1 capitalize">
                {{ getDayName(selectedDate) }}
              </div>
              <div class="text-xs text-gray-600 mb-4">{{ formatDate(selectedDate) }}</div>
              <div class="bg-[#18b7bd] text-white rounded-md w-full py-2 text-sm text-center">
                {{ selectedSlots[selectedDate] }}
              </div>
              <button class="text-[#18b7bd] mt-3 text-sm hover:underline" @click="resetSelection">
                Modifier le créneau
              </button>
            </div>
          </template>

          <template v-else>
            <div
              v-for="(day, index) in visibleFilteredDays"
              :key="index"
              class="flex flex-col items-center min-w-[80px]"
            >
              <div class="text-xs text-gray-900 font-normal mb-1 capitalize">
                {{ day.name }}
              </div>
              <div class="text-xs text-gray-600 mb-4">{{ formatDate(day.date) }}</div>

              <div v-if="day.timeslots.length > 0" class="w-full">
                <button
                  v-for="(slot, idx) in day.timeslots"
                  :key="idx"
                  type="button"
                  class="bg-gray-100 rounded-md w-full py-2 text-sm text-gray-900 mb-2 cursor-pointer"
                  :class="{
                    'bg-[#18b7bd] text-white': selectedSlots[day.date] === slot,
                    'hover:bg-indigo-200': selectedSlots[day.date] !== slot,
                  }"
                  @click="selectSlot(day.date, slot)"
                >
                  {{ slot }}
                </button>
              </div>
              <div v-else class="text-xs text-gray-400 italic text-center">
                Aucun créneau disponible
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/fr';


moment.locale('fr');

export default {
  props: {
    selectedServices: Array,
    firmId: String,
  },
  data() {
    return {
      days: [],
      currentWeekIndex: 0,
      selectedSlots: {},
      selectedDate: null,
      selectedEmployeId: '',
      employes: [],
      isLoading: false,
      allDisponibilitesByDay: [],
      isMobile: false,
    };
  },
  computed: {
    selectedServiceIds() {
      return this.selectedServices.map((s) => s._id);
    },
    daysPerPage() {
      return this.isMobile ? 3 : 7;
    },
    visibleDays() {
      const startIndex = this.currentWeekIndex * this.daysPerPage;
      return this.days.slice(startIndex, startIndex + this.daysPerPage);
    },
    visibleFilteredDays() {
      const startIndex = this.currentWeekIndex * this.daysPerPage;
      return this.filteredDays.slice(startIndex, startIndex + this.daysPerPage);
    },
    isPreviousDisabled() {
      const firstVisibleDay = this.visibleDays[0];
      const today = moment().startOf('day');
      return firstVisibleDay && moment(firstVisibleDay.date).isBefore(today);
    },
    filteredDays() {
      return this.allDisponibilitesByDay.map((day) => {
        let filteredDisponibilites = this.selectedEmployeId
          ? day.disponibilites.filter((d) => d.employe._id === this.selectedEmployeId)
          : day.disponibilites;

        let timeslots = filteredDisponibilites.flatMap((d) => d.timeslots).sort((a, b) => a.localeCompare(b));

        return {
          date: day.date,
          name: day.name,
          timeslots,
        };
      });
    },
  },
  mounted() {
    this.checkDevice();
    window.addEventListener('resize', this.checkDevice);
    this.fetchDisponibilites();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkDevice);
  },
  methods: {
    checkDevice() {
      this.isMobile = window.innerWidth < 768;
    },
    selectEmploye(id) {
      this.selectedEmployeId = this.selectedEmployeId === id ? '' : id;
    },
    clearSelectedEmploye() {
      this.selectedEmployeId = '';
    },
    stringToColor(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      let color = '#';
      for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += ('00' + value.toString(16)).slice(-2);
      }
      return color;
    },
    changeWeek(direction) {
      const totalWeeks = Math.ceil(this.days.length / this.daysPerPage);
      this.currentWeekIndex = (this.currentWeekIndex + direction + totalWeeks) % totalWeeks;
    },
    selectSlot(date, slot) {
      this.selectedSlots = { [date]: slot };
      this.selectedDate = date;

      let employeId = this.selectedEmployeId;

      if (!employeId) {
        const dayData = this.allDisponibilitesByDay.find((d) => d.date === date);
        if (dayData) {
          const candidats = dayData.disponibilites.filter((d) => d.timeslots.includes(slot));
          if (candidats.length > 0) {
            const randomIndex = Math.floor(Math.random() * candidats.length);
            employeId = candidats[randomIndex].employe._id;
          }
        }
      }

      this.$emit('creneau-selectionne', {
        date,
        slot,
        employeId,
      });
    },
    resetSelection() {
      this.selectedSlots = {};
      this.selectedDate = null;
    },
    getDayName(date) {
      return moment(date).format('dddd');
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('D MMMM YYYY');
    },
    async fetchDisponibilites() {
      if (this.isLoading) return;
      this.isLoading = true;

      this.allDisponibilitesByDay = [];
      this.employes = [];

      const baseDate = moment().startOf('day');
      const next28Days = Array.from({ length: 28 }, (_, i) => baseDate.clone().add(i, 'days'));

      for (const date of next28Days) {
        try {
          const res = await axios.post('https://b53c7d5edf32.ngrok-free.app/api/appointment/disponibilites', {
            firmId: this.firmId,
            serviceIds: this.selectedServiceIds,
            date: date.format('YYYY-MM-DD'),
          });

          const disponibilites = res.data.disponibilites || [];

          disponibilites.forEach((entry) => {
            if (!this.employes.some((e) => e._id === entry.employe._id)) {
              this.employes.push(entry.employe);
            }
          });

          this.allDisponibilitesByDay.push({
            date: date.format('YYYY-MM-DD'),
            name: date.format('dddd'),
            disponibilites,
          });

          this.days.push({
            date: date.format('YYYY-MM-DD'),
            name: date.format('dddd'),
          });
        } catch (error) {  if (error.response && error.response.status === 401) {
      // Supprime le token et redirige vers la page de connexion
      localStorage.removeItem('adminToken');
      this.$router.push('/login'); // ou '/login' selon ta route
    }
          console.error('Erreur lors de la récupération des disponibilités :', error);
        }
      }

      this.isLoading = false;
    },
  },
};
</script>
